import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { logger } from 'Services/logger/logger';
import { useRefetchSaleTimes } from 'Stores/SaleTimesStore/SaleTimesStore';
import { TIME_IN_MS } from 'Utilities/consts';
import useIsAuth from 'Utilities/hooks/useIsAuth';

const { publicRuntimeConfig } = getConfig();

const { SALE_DISABLED_SALE_TIMES_MAXIMUM_JITTER, SALE_DISABLED_SALE_TIMES_POLLING_INTERVAL } = publicRuntimeConfig;

const SALE_DISABLED_MINIMUM_POLLING_INTERVAL = Number.isFinite(Number(SALE_DISABLED_SALE_TIMES_POLLING_INTERVAL))
	? Number(SALE_DISABLED_SALE_TIMES_POLLING_INTERVAL)
	: TIME_IN_MS.ONE_MINUTE;

const MAX_JITTER = Number.isFinite(Number(SALE_DISABLED_SALE_TIMES_MAXIMUM_JITTER))
	? Number(SALE_DISABLED_SALE_TIMES_MAXIMUM_JITTER)
	: TIME_IN_MS.THIRTY_SECONDS;

export const SaleTimeSynchroniser: React.FC = () => {
	const { pathname } = useRouter();
	const isAuth = useIsAuth();
	const refetchSaleTimes = useRefetchSaleTimes();

	// This prevents all of our users from fetching at the same time
	const saleDisabledJitterTime = useMemo(() => Math.floor(Math.random() * MAX_JITTER), []);

	// This query refetches the sale times every 30 minutes and on tab focus
	// When sale is disabled during sale hours it will refetch more frequently
	const { refetch } = useQuery({
		enabled: isAuth,
		onError: (error: AxiosError) => {
			const message = error?.message || 'There was an issue with fetching sale times';
			logger.error({
				context: { extra: { info: message } },
				error,
				message,
				scope: 'SaleTimeSynchroniser',
			});
		},
		queryFn: refetchSaleTimes,
		queryKey: ['saleTimes'],
		refetchInterval: (saleTimes) => {
			if (saleTimes?.isSaleActive && !saleTimes?.isLiveSaleEnabled) {
				return SALE_DISABLED_MINIMUM_POLLING_INTERVAL + saleDisabledJitterTime;
			}

			return TIME_IN_MS.THIRTY_MINUTES;
		},
		refetchOnWindowFocus: 'always',
		staleTime: Infinity,
	});

	useEffect(() => {
		// This refetches the sale times on route change
		if (isAuth) {
			void refetch();
		}
	}, [pathname, refetch, isAuth]);

	return null;
};
